import { useLocalSearchParams } from 'expo-router'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { useCustomer, useUpdateCustomer } from '@/hooks/customers'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useCustomerForm } from '@/hooks/useCustomerForm'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { errorToString } from '@/lib/errors'
import { notify } from '@/lib/notice'
import type { CustomerFormData } from '@/types/customers'
import { CustomerFormFields } from '@/ui/customers/CustomerFormFields'
import { FormErrorSummary } from '@/ui/form'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { NotFound } from '@/ui/NotFound'
import { Page } from '@/ui/Page'

export default function CustomerEditPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{ orgSlug: string; customerSlug: string }>()
    const { navigateBackTo } = useNavigateBack()
    const { customer, isPending } = useCustomer(params.customerSlug)
    const { updateCustomer } = useUpdateCustomer()

    const {
        control,
        handleSubmit,
        setError,
        formState: { isSubmitting, errors, isSubmitted, isDirty },
    } = useCustomerForm({
        existingCustomer: customer as CustomerFormData,
        onSubmit: async (data: CustomerFormData) => {
            const customerWithExpand = customer as CustomerFormData
            if (!customerWithExpand?.id) {
                setError('name', { type: 'manual', message: 'Customer ID missing' })
                return
            }

            try {
                await updateCustomer({
                    customerId: customerWithExpand.id,
                    data,
                })
                notify.success('Customer updated successfully')
                navigateBackTo(`/${params.orgSlug}/settings/customers`)
            } catch (error) {
                const message = errorToString(error)
                setError('name', { type: 'manual', message })
            }
        },
    })

    useHeaderOptions({
        buttons: {
            type: 'save',
            onPress: handleSubmit,
            loading: isSubmitting,
            loadingTitle: 'Saving...',
        },
        title: customer ? `Edit ${customer.name}` : undefined,
        enabled: !!customer,
    })

    if (isPending) {
        return <LoadingIndicator />
    }

    if (!customer) {
        return (
            <NotFound message="Customer not found. It may have been deleted or you don't have permission to edit it." />
        )
    }

    return (
        <Page>
            <FormErrorSummary errors={errors} isEnabled={isSubmitted} />
            <CustomerFormFields control={control} />
            <UnsavedChangesDialog isDirty={isDirty} />
        </Page>
    )
}
